/* ComplianceConsentModal — appears once after login/registration when the user
 * has not yet accepted the current consent version. Also used as a pre-flight
 * gate before critical actions (clinical analysis, lab upload) via `force`.
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useConsentStatus, useComplianceCatalog, acceptConsent } from "@/lib/compliance";
import { ShieldCheck, Sparkles, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const CONSENT_ITEMS = [
  { id: "non_medical",    icon: AlertTriangle, color: "text-rose-600",
    label_bg: "Разбирам, че Nutramilo не е медицинско устройство",
    label_en: "I understand Nutramilo is not a medical device",
    desc_bg: "Не поставя диагноза и не предписва лечение. Резултатите са насоки, не медицинско мнение.",
    desc_en: "It does not diagnose or prescribe treatment. Outputs are guidance, not medical opinions." },
  { id: "ai_limitations", icon: Sparkles, color: "text-purple-600",
    label_bg: "Разбирам ограниченията на AI",
    label_en: "I understand AI limitations",
    desc_bg: "AI генерира вероятностни изводи; качеството зависи от подадените данни. Evidence layer намалява, но не премахва несигурността.",
    desc_en: "AI produces probabilistic outputs; quality depends on input data. The evidence layer reduces but does not eliminate uncertainty." },
  { id: "data_usage",     icon: ShieldCheck, color: "text-sky-600",
    label_bg: "Съгласявам се с обработката на моите данни",
    label_en: "I consent to processing my data",
    desc_bg: "Здравните данни се използват единствено за вашия анализ — никога за AI обучение без изричен opt-in. Виж Политика за поверителност.",
    desc_en: "Health data is used only for your analysis — never for AI training without explicit opt-in. See Privacy Policy." },
];

export default function ComplianceConsentModal({ open: openProp, onClose, force = false }) {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const { status, refresh, loading } = useConsentStatus();
  const { catalog } = useComplianceCatalog();
  const [saving, setSaving] = useState(false);
  const [checks, setChecks] = useState({ non_medical: false, ai_limitations: false, data_usage: false });
  const [dismissed, setDismissed] = useState(false);

  // Auto-open when user is logged in and consent not up-to-date (first login)
  const autoOpen = !loading && user && status && status.up_to_date === false && !dismissed;
  const open = force ? true : (openProp ?? autoOpen);

  useEffect(() => {
    if (open && status?.items) {
      setChecks({
        non_medical: !!status.items.non_medical,
        ai_limitations: !!status.items.ai_limitations,
        data_usage: !!status.items.data_usage,
      });
    }
  }, [open, status]);

  if (!open) return null;

  const allChecked = CONSENT_ITEMS.every(i => checks[i.id]);

  const handleAccept = async () => {
    if (!allChecked) return;
    setSaving(true);
    try {
      await acceptConsent(API, CONSENT_ITEMS.map(i => i.id));
      toast.success(bg ? "Благодарим ви — условията са приети." : "Thank you — terms accepted.");
      await refresh();
      onClose?.(true);
    } catch (_err) {
      toast.error(bg ? "Грешка при запис. Опитайте отново." : "Failed to save. Please retry.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (force) return; // cannot dismiss a mandatory gate
    setDismissed(true);
    onClose?.(false);
  };

  return (
    <div
      data-testid="compliance-consent-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-slate-900 dark:text-slate-100 p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {!force && (
          <button
            onClick={handleClose}
            aria-label={bg ? "Затвори" : "Close"}
            className="absolute top-3 right-3 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-slate-400"
            data-testid="compliance-modal-close"
          >
            <X size={18} />
          </button>
        )}

        <div className="flex items-center gap-2 mb-3">
          <div className="rounded-lg bg-sky-100 p-2"><ShieldCheck className="text-sky-700" size={18} /></div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
            {bg ? "Преди да продължим" : "Before we continue"}
          </h2>
        </div>

        {catalog?.intended_use && (
          <p className="text-xs text-gray-600 dark:text-slate-400 leading-snug mb-4 border-l-2 border-sky-300 pl-3">
            {catalog.intended_use}
          </p>
        )}

        <div className="space-y-3 mb-4">
          {CONSENT_ITEMS.map(item => {
            const Icon = item.icon;
            return (
              <label
                key={item.id}
                className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-slate-700 p-3 hover:bg-gray-50 dark:bg-white/5 cursor-pointer"
                data-testid={`consent-row-${item.id}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  checked={!!checks[item.id]}
                  onChange={e => setChecks(c => ({ ...c, [item.id]: e.target.checked }))}
                  data-testid={`consent-checkbox-${item.id}`}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-slate-100">
                    <Icon size={14} className={item.color} />
                    {bg ? item.label_bg : item.label_en}
                  </div>
                  <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5 leading-snug">
                    {bg ? item.desc_bg : item.desc_en}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
          <a
            href="/legal/ai-transparency"
            className="text-xs text-sky-700 hover:underline"
            target="_blank" rel="noreferrer"
          >
            {bg ? "AI Прозрачност и ограничения →" : "AI Transparency & Limitations →"}
          </a>
          <button
            onClick={handleAccept}
            disabled={!allChecked || saving}
            data-testid="consent-accept-btn"
            className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors ${
              allChecked && !saving ? "bg-sky-600 hover:bg-sky-700" : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {saving
              ? (bg ? "Записване…" : "Saving…")
              : (bg ? "Приемам и продължавам" : "Accept & continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
